import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { MEDIA_BREAKPOINTS } from 'shared/design-system/settings/variables/Breakpoints';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactAnimation from 'shared/design-system/base/TrueImpactAnimation';
import { SectionBase } from 'npo/pages/ReportBuilder/Components/SectionBase';
import SearchAutocomplete from 'npo/pages/ReportBuilder/Components/Location/SearchAutocomplete';
import BeneficiarySingleValue from 'npo/pages/ReportBuilder/Components/Beneficiary/BeneficiaryValues/BeneficiarySingleValue';
import AddressInput from 'shared/components/AddressInput';
import { faMapPin } from '@fortawesome/pro-light-svg-icons';
import { GuidanceTextResponse } from '../utils';

const LocationContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.extraLarge};
  > * {
    width: 100%;
  }
  ${MEDIA_BREAKPOINTS.largerThanMedium} {
    flex-direction: row;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  align-self: start;
`;

const EmptyState = styled.div`
  text-align: center;
  background-color: ${COLORS.greyLight};
`;

const EmptyStateInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACERS.small};
  padding: ${SPACERS.medium} ${SPACERS.extraLarge};
  svg {
    font-size: 32px;
  }
`;

const Header = styled.div`
  color: ${COLORS.whiteBase};
  background-color: ${COLORS.ctaPrimary};
  padding: ${SPACERS.small};
  border-radius: 4px 4px 0 0;
`;

const SelectedContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
`;

interface SectionAddressProps {
  title?: string;
  subTitle?: string;
  helpText?: string;
  guidanceText: GuidanceTextResponse;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  questionId?: number | undefined;
  value?: string;
  onChange: (
    questionId: SectionAddressProps['questionId'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ) => unknown;
  locationsSelected?: boolean;
}

const SectionAddress: React.FC<SectionAddressProps> = ({
  title,
  subTitle,
  helpText,
  guidanceText,
  error,
  questionId,
  value,
  onChange,
  locationsSelected = true,
}) => {
  const handleChange = useCallback(
    event => {
      onChange(questionId, [
        {
          address: event.target.value,
        },
      ]);
    },
    [questionId, onChange],
  );
  const handleLatLng = useCallback(
    latLng => {
      onChange(questionId, [
        {
          ...latLng,
          percent: 100,
        },
      ]);
    },
    [questionId, onChange],
  );
  useEffect(() => {
    if (typeof value !== 'string') {
      return;
    }
    onChange(
      questionId,
      JSON.parse(
        value.replace(/\'/g, '"'), // eslint-disable-line
      ),
    );
  }, [value, onChange]); // eslint-disable-line
  return (
    <SectionBase
      title={title}
      subTitle="Where does your program operate?"
      helpText="You can add as many locations as you need. Cities, states, countries and specific addresses are all acceptable."
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      <LocationContainer>
        <TrueImpactAnimation variant="appear" animationDelay={5}>
          <SearchContainer>
            <AddressInput
              value={Array.isArray(value) ? value[0].address : ''}
              onChange={handleChange}
              onLatLng={handleLatLng}
            />
            <SearchAutocomplete />
          </SearchContainer>
        </TrueImpactAnimation>
        <TrueImpactAnimation variant="appear" animationDelay={5}>
          {!locationsSelected && (
            <SelectedContainer>
              <EmptyState>
                <Header>Locations</Header>
                <EmptyStateInner>
                  <FontAwesomeIcon icon={faMapPin} />
                  <TrueImpactText color="greyDark">
                    Your selected locations will appear here.
                  </TrueImpactText>
                </EmptyStateInner>
              </EmptyState>
            </SelectedContainer>
          )}
          {locationsSelected && (
            <SelectedContainer>
              {/* <BeneficiarySingleValue
                title="Locations"
                buttonLabel="Locations"
                inputLabel="Dallas, TX"
              /> */}
              <TrueImpactText variant="body" align="center">
                Please let us know what percentage of your program’s services
                and impacts occur in each location. Approximations are totally
                fine!
              </TrueImpactText>
              <TrueImpactText variant="body" align="center">
                <strong>An example:</strong> If one of your locations serves 25
                youth out of a total of 100 youth served by the program, then
                that location would account for 25 percent.
              </TrueImpactText>
            </SelectedContainer>
          )}
        </TrueImpactAnimation>
      </LocationContainer>
    </SectionBase>
  );
};

export default SectionAddress;
